import { useContext, useEffect, useState} from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import UserContext from "./UserContext";
import useSWR from "swr";
import Fetcher from "../lib/Fetcher";
import ConsentModal from "./ConsentModal";

 const Guard = () => {
  const { session, setSession } = useContext(UserContext);
  const location = useLocation();
  const { data, error, isLoading, mutate} = useSWR("/auth/session", Fetcher);
  const path = location.pathname;
  const [showConsentModal, setShowConsentModal] = useState(false);
  useEffect(() => {
    let timer;
    
    if (data) {
      setSession(data);
      
      // Check if user requires consent (OAuth users or users who haven't accepted)
      if (data.requiresConsent && (data.role === 'user' || data.role === 'admin')) {
        setShowConsentModal(true);
      } else {
        setShowConsentModal(false);
      }
    }
    
    if (error) {
      // Delay setting session to false to handle race conditions
      // SWR fetches /auth/session immediately.
      // If the cookie hasn't propagated fully yet, server sends 401.
      // Guard quickly sets session = false, redirecting you.
      // But shortly after, a valid session could be fetched. By delaying setSession(false), 
      // you're giving time for the cookie to settle and SWR to retry once.
      // It's a soft debounce against false-negative 401s right after login.
      // Increased delay to allow SWR to retry multiple times (SWR retries on error)
      timer = setTimeout(() => {
        // Only set session to false if we still have an error after delay
        // This allows SWR to retry and potentially succeed
        if (error && !data) {
          setSession(false);
        }
      }, 1000); // Increased to 1 second to allow SWR retries
    }
    
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [data, error, setSession]);

  const handleConsentAccepted = async () => {
    // Refresh session to get updated consent status
    await mutate();
    setShowConsentModal(false);
  };


  // Show loading state while checking session
  if(isLoading || session === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-l from-dark-100 to-primary py-6 shadow-[0_-4px_12px_rgba(0,0,0,0.2)] px-4" style={{ background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 50%, #F3F4F6 100%)' }}>
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 drop-shadow-lg">Loading session...</h1>
      </div>
    );
  }

  // Only redirect if session is explicitly false (not just falsy)
  // This prevents redirects when session is still being validated
  if(session === false) {
    return <Navigate to="/login" replace />;
  }
   
  // Safety check: if session is falsy or missing role, redirect to login
  // But only if we're not still loading (isLoading check above handles that)
  if (!session || !session.role) {
    // Double-check we're not in a loading state before redirecting
    if (!isLoading) {
      return <Navigate to="/login" replace />;
    }
    // If still loading, show loading state instead
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-l from-dark-100 to-primary py-6 shadow-[0_-4px_12px_rgba(0,0,0,0.2)] px-4" style={{ background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 50%, #F3F4F6 100%)' }}>
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 drop-shadow-lg">Loading session...</h1>
      </div>
    );
  }

  // Allow business partners (users with approved trainer account) to access trainer routes
  // Allow all users (members) to access student routes
  // Business partners can toggle between member and professional dashboards
  if (path.startsWith("/trainer") && session.role === "user") {
    // Check if user is an approved business partner
    if (session.isTrainer && session.trainerStatus === "active") {
      // Allow access to trainer routes for approved business partners
      return <Outlet />;
    } else {
      // Block access if not an approved business partner
      return <Navigate to="/student/home" replace />;
    }
  }

  // Block non-admin from accessing /admin routes
  if (path.startsWith("/admin") && session.role !== "admin" && session.role !== "super_admin") {
    if (session.role === "trainer") {
      return <Navigate to="/trainer/home" replace />;
    }
    if (session.role === "customer") {
      return <Navigate to="/orders" replace />;
    }
    if (session.role === "user") {
      return <Navigate to="/student/home" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // Handle Customer Role restrictions
  if (session.role === "customer") {
    const allowedCustomerPaths = [
      "/student/orders",
      "/student/profile",
      "/student/help",
      "/checkout",
      "/student/checkout",
      "/orders",
      "/student/orders"
    ];

    // If customer tries to access dashboard or specific classes etc.
    if (path.startsWith("/student") || path.startsWith("/trainer")) {
      const isAllowed = allowedCustomerPaths.some(p => path.startsWith(p));
      if (!isAllowed) {
        return <Navigate to="/orders" replace />;
      }
    }
  }

  // Block admin from accessing /student and /trainer routes
  if ((path.startsWith("/student") || path.startsWith("/trainer")) && (session.role === "admin" || session.role === "super_admin")) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Allow trainers to access student routes - they may be enrolled as students with other trainers
  // The toggle dashboard feature allows trainers to switch between trainer and student views

  // Show consent modal if required (blocks access until consent is accepted)
  if (showConsentModal && session && session.role) {
    return (
      <>
        <ConsentModal onConsentAccepted={handleConsentAccepted} />
        {/* Block access by not rendering Outlet */}
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
          <p className="text-gray-600">Please accept the Terms of Service and Privacy Policy to continue.</p>
        </div>
      </>
    );
  }

  return (<Outlet />);

};

export default Guard;